import { compose, mdsWidget } from 'dojo/_base/declare';
import template from './ScenarioEditor.html?raw';

import _StatefulSection from '../../../moreCore/UI/_StatefulSection/_StatefulSection';
import _uiFrameMixin from '../../../moreCore/UI/Container/uiFrame';
import { TabContainer } from "../../../moreCore/_loadingHelpers/Tabbing";
import { Block, HTScenarioBlock, HtScenarioExternalUrl, Scenario, ExternalUrl, HtBlockEvBooking, HtEndpointScenario } from "../ScreenplayTypes";
import lang from 'dojo/_base/lang';
import GenericDialog from '../../../moreCore/UI/GenericDialog/GenericDialog';
import SearchSelect from '../../../moreCore/UI/SearchSelect/SearchSelect';
import { ExpandableSection, ExpandableSectionMarkings } from "../../../moreCore/UI/Container/ExpandableSectionTypes";
import EntityStore from '../../../moreCore/Tools/EntityStore';
import StaticTable from '../../../moreCore/UI/StaticTable/StaticTable';
import CheckBox from '../../../moreCore/UI/CheckBox/CheckBox';
import BlockEditor from '../Block/BlockEditor';
import ExternalUrlEditor from '../ExternalUrl/ExternalUrlEditor';
import { StringHelpers } from "../../../more/Helpers";
import { uiFrameTypes } from "../../../moreCore/UI/Container/uiFrameTypes";
import _GlobalSelectEntityDialogsMixin from '../../_loadingHelpers/_GlobalSelectEntityDialogsMixin';
import FloatingMenu from '../../../moreCore/UI/Floating/Menu';
import { SelectEntityDialogTypes } from "../../../moreCore/_loadingHelpers/SelectEntityDialog";

class ScenarioEditor extends compose(_StatefulSection, _uiFrameMixin, _GlobalSelectEntityDialogsMixin) {
    templateString: any = template;

    private inputSource: MdsLax<InstanceType<typeof SearchSelect>>;
    private sectionTableContainer: HTMLDivElement;
    private embeddedEditorContainer: HTMLDivElement;
    private embeddedEditor: MdsLax<InstanceType<typeof BlockEditor>> | MdsLax<InstanceType<typeof ExternalUrlEditor>>;
    private htEntity: HtEndpointScenario;


    private tableContainer: MdsLax<InstanceType<typeof ExpandableSection>>;
    private store: MdsLax<InstanceType<typeof EntityStore<HtScenarioExternalUrl | HTScenarioBlock>>>;
    private table: MdsLax<InstanceType<typeof StaticTable>>;

    private buttonNew: HTMLButtonElement;

    isNew: boolean = false;
    reloadAfterSave = true;

    setIsNew(isNew: boolean) {
        this.isNew = isNew;
        this.enableButton("save");
    }

    setHtEntity(entity: HtEndpointScenario) {
        this.htEntity = entity;
    }

    getHtEntity(): HtEndpointScenario {
        return this.htEntity;
    }

    private rowConfigBlocks: StaticTable.RowConfig = {
        attributes: [
            null,
            //"order",
            "ref_screenplay_block.$Displayname"
        ],
        headings: [
            (this as any).translate("General_Label_Edit"),
            //this.translate("General_Label_Order"),
            (this as any).translate("Scenario_Label_Block")
        ],
        colNamesInternal: [
            "edit",
            //"order",
            "ref_screenplay_block.$Displayname",
        ],
        bgColors: [
            null,
            //null
            null
        ],
        colspans: [
            1,
            //1,
            5
        ]
    }

    private rowConfigExternalUrls: StaticTable.RowConfig = {
        attributes: [
            null,
            //"order",
            "ref_screenplay_external_url.$Displayname"
        ],
        headings: [
            (this as any).translate("General_Label_Edit"),
           //this.translate("General_Label_Order"),
            (this as any).translate("Scenario_Label_ExternalUrl")
        ],
        colNamesInternal: [
            "edit",
            //"order",
            "ref_screenplay_external_url.description"
        ],
        bgColors: [
            null,
            //null,
            null
        ],
        colspans: [
            1,
            //1,
            5
        ]
    }

    setReloadAfterSave(reload: boolean) {
        this.reloadAfterSave = reload;
    }

    private tableLayoutBlocks: StaticTable.Layout = {
        name: "blockTable",
        label: "blockTable",
        rows: [(this as any).rowConfigBlocks],
    }

    private tableLayoutExternalUrls: StaticTable.Layout = {
        name: "externalUrlTable",
        label: "externalUrlTable",
        rows: [(this as any).rowConfigExternalUrls],
    }

    private _setTableLayout(source: string) {
        switch (source) {
            case "blocks":
                this.table.removeLayout(this.tableLayoutExternalUrls);
                this.table.setTableLayout(this.tableLayoutBlocks);
                break;
            case "external_urls":
                this.table.removeLayout(this.tableLayoutBlocks);
                this.table.setTableLayout(this.tableLayoutExternalUrls)
                break;
        }
        this._resetSelectedEntities();
    }

    private _setTableStore(source: string) {
        let entities;
        switch (source) {
            case "blocks":
                entities = this.getAttributeValue("EL_ht_screenplay_scenario_block");
                break;
            case "external_urls":
                entities = this.getAttributeValue("EL_ht_screenplay_scenario_ext_urls");
                break;
        }
        this.store.setup(entities);
        this._setExpanderMarking();
        this._setNewButtonState();
    }

    private _enableCallback(entity: HtScenarioExternalUrl | HTScenarioBlock, rowGroup: StaticTable.StaticTableRowGroup<HtScenarioExternalUrl | HTScenarioBlock>): boolean {
        if (this.embeddedEditor) return false;

        return true;
    }

    private setupTable() {
        let source = this.getAttributeValue("source");
        let tableLayout;
        if (this.table) this.table.destroy();

        this.store = new EntityStore();
        this.store.on("change", lang.hitch(this, this._onStoreChange));
        this.table = new StaticTable();
        this._setTableLayout(source);
        this.table.addDefaultRowActionButton("rowsorting", null, lang.hitch(this, this._enableCallback));
        this.table.addDefaultRowActionButton("delete", lang.hitch(this, this._onClickTableDelete), lang.hitch(this, this._enableCallback));
        this.table.setFormatter(lang.hitch(this, this._tableFormatter));
        this.table.setupFromStore(this.store);
        this.table.setCyAttribute("ScenarioEditorTable");
        this.table.placeAt(this.tableContainer);
    }

    private _tableFormatter(cell: StaticTable.StaticTableCell<HtScenarioExternalUrl | HTScenarioBlock>): boolean {
        let name = cell.name;
        let entity = cell.entity;
        let node = cell.tableCellNode;

        switch (name) {
            case "edit":
                node.innerHTML = "";
                let checkbox: MdsLax<InstanceType<typeof CheckBox>> = new CheckBox();
                checkbox.setValue(entity.selected ? true : false, true);
                checkbox.on("change", lang.hitch(this, this._onSelectedCheckbox, entity));
                checkbox.placeAt(node);
                let changedDiffData = this.getAttributeValue("changedDiffData");
                let deletedDiffData = this.getAttributeValue("deletedDiffData");
                if ((changedDiffData && changedDiffData.length > 0) || (deletedDiffData && deletedDiffData.length > 0)) checkbox.setReadonly(true);
                return true;
        }
        return false;
    }

    private _onSelectedCheckbox(entity: HtScenarioExternalUrl | HTScenarioBlock, value: boolean) {
        let completeList = this.store.getCurrentCompleteList();
        if (value) {
            entity.selected = value;
            for (let currentEntity of completeList) {
                if (entity.ID !== currentEntity.ID) currentEntity.selected = false;
            }

            let source = this.getAttributeValue("source");
            let embeddedEntity: Block | ExternalUrl;
            if (!source) return;
            switch (source) {
                case "blocks":
                    let htBlock: HTScenarioBlock = entity as HTScenarioBlock;
                    embeddedEntity = htBlock.ref_screenplay_block;
                    break;
                case "external_urls":
                    let htUrl: HtScenarioExternalUrl = entity as HtScenarioExternalUrl;
                    embeddedEntity = htUrl.ref_screenplay_external_url;
                    break;
            }

            this._embedEditor(embeddedEntity);
        } else {
            entity.selected = value;
            this._closeEditor();
        }
        this.table.updateTable();
    }

    private _onClickTableDelete(rowGroup: StaticTable.StaticTableRowGroup<HTScenarioBlock | HtScenarioExternalUrl>) {
        if (this.embeddedEditor) {
            let source = this.getAttributeValue("source");
            let refEntity = this.embeddedEditor.getAttributeValue("___root");
            switch (source) {
                case "blocks":
                    let htBlock: HTScenarioBlock = rowGroup.entity as HTScenarioBlock;
                    if (htBlock.ref_screenplay_block.ID === refEntity.ID) {
                        this._resetEmbeddedEditor();
                    }
                    break;
                case "external_urls":
                    let htExternalUrl: HtScenarioExternalUrl = rowGroup.entity as HtScenarioExternalUrl;
                    if (htExternalUrl.ref_screenplay_external_url.ID === refEntity.ID) {
                        this._resetEmbeddedEditor();
                    }
                    break;
            }
        }
    }

    private _onStoreChange() {
        this._setExpanderMarking();
        let changedDiffData = this.store.getCurrentCompleteList("changed");
        this.setAttributeValue("changedDiffData", changedDiffData.length > 0 ? changedDiffData : null);

        let deletedDiffData = this.store.getCurrentCompleteList("deleted");
        this.setAttributeValue("deletedDiffData", deletedDiffData.length > 0 ? deletedDiffData : null);

        this._setNewButtonState();
    }

    private _tableHasContent(): boolean {
        return this.store.getCurrentCompleteList().length > 0;
    }

    private _setExpanderMarking(): void {
        this.tableContainer.setMarking(this._tableHasContent() ? ExpandableSectionMarkings.hasContent : ExpandableSectionMarkings.default);
    }

    private _setNewButtonState(): void {
        let storeDirty = this.store.isDirty();
        this.disableNode(this.buttonNew, storeDirty);
    }

    private _resetSelectedEntities() {
        if (!this.store) return;
        let completelist = this.store.getCurrentCompleteList();
        for (let entity of completelist) entity.selected = false;
        this.table.updateTable();
    }

    private _setSelectedEntity(entity: Block | ExternalUrl) {
        let htEntity = this._getHtEntity(entity);
        if (!htEntity) return;
        this._resetSelectedEntities();
        htEntity.selected = true;
        this.table.updateTable();
    }

    private _getHtEntity(entity: Block | ExternalUrl): HTScenarioBlock | HtScenarioExternalUrl {
        let source = this.getAttributeValue("source");
        if (!entity) return;

        if (source) {
            switch (source) {
                case "blocks":
                    let completelistBlocks: Array<HTScenarioBlock> = this.store.getCurrentCompleteList() as Array<HTScenarioBlock>;
                    for (let htBlock of completelistBlocks) {
                        if (htBlock.ref_screenplay_block.ID === entity.ID) return htBlock;
                    }
                    break;
                case "external_urls":
                    let completelistUrls: Array<HtScenarioExternalUrl> = this.store.getCurrentCompleteList() as Array<HtScenarioExternalUrl>;
                    for (let htUrl of completelistUrls) {
                        if (htUrl.ref_screenplay_external_url.ID === entity.ID) return htUrl;
                    }
                    break;
            }
        }


        return null
    }

    private _removeEntityFromStore(entity: Block | ExternalUrl) {
        let htEntity = this._getHtEntity(entity);
        this.store.deleteEntry(htEntity);
    }

    private _closeEditor(entity?: Block | ExternalUrl, isNew?: boolean, closeCallback?: Function, saveCallback?: Function) {
        if (this.embeddedEditor && this.embeddedEditor.isNew) this.embeddedEditor.forceSetDirty(true);
        if (this.embeddedEditor && (this.embeddedEditor.isDirty())) {
            let closeFunction = this.embeddedEditor.on("closed", lang.hitch(this, this._afterClose, entity, isNew, closeCallback));
            let saveFunction = this.embeddedEditor.on("saved", lang.hitch(this, this._onClickReloadList, saveCallback));
            this.embeddedEditor.on("canceled", lang.hitch(this, this._onCallbackCancel, closeFunction, saveFunction));
            if (this.embeddedEditor.isNew) this.embeddedEditor.on("notSaved", lang.hitch(this, this._removeEntityFromStore, this.embeddedEditor.getAttributeValue("___root")));

            let currentEntity = this.embeddedEditor.getCurrentData();
            this._setSelectedEntity(currentEntity);
        }
        if (this.embeddedEditor && !this.embeddedEditor._onCloseRequest()) {
            return;
        } else {
            this._resetEmbeddedEditor();
            if (entity) this._embedEditor(entity, isNew);
        }
    }

    private _onCallbackCancel(closeFunction: dojo.WatchHandle, saveFunction: dojo.WatchHandle) {
        if (closeFunction) closeFunction.remove();
        if (saveFunction) closeFunction.remove();
    }

    private _afterClose(entity?: Block | ExternalUrl, isNew?: boolean, callback?: Function) {
        this._resetEmbeddedEditor();
        if (entity) this._setSelectedEntity(entity);
        else this._resetSelectedEntities();

        if (entity || isNew) this._embedEditor(entity, isNew);

        if (callback) callback();
    }

    private _embedEditor(entity: Block | ExternalUrl, isNew: boolean = false, closeCallback?: Function, saveCallback?: Function) {
        if (this.embeddedEditor) {
            this._closeEditor(entity, isNew, closeCallback, saveCallback);
            return;
        }

        this._resetEmbeddedEditor();

        if (isNew && !this._getHtEntity(entity)) {
            this._createNewHtEntity(entity);
            this._setSelectedEntity(entity);
        }

        let source = this.getAttributeValue("source");

        switch (source) {
            case "blocks":
                entity = entity as Block;
                if (entity) {
                    this.hideNode(this.embeddedEditorContainer, false);
                    this.embeddedEditor = new BlockEditor();
                    this.embeddedEditor.setIsNew(isNew);
                    this.embeddedEditor.setReloadAfterSave(false);
                    this.embeddedEditor.setup(null, entity, "node", this.embeddedEditorContainer);
                    if (isNew) this.embeddedEditor.on("clickedSave", lang.hitch(this, this._setScenario));
                } else if ((!entity) && isNew) {
                    this.hideNode(this.embeddedEditorContainer, false);
                    this.embeddedEditor = new BlockEditor();
                    this.embeddedEditor.setIsNew(isNew);
                    this.embeddedEditor.setReloadAfterSave(false);
                    this.embeddedEditor.setup(null, null, "node", this.embeddedEditorContainer);
                    this.embeddedEditor.on("clickedSave", lang.hitch(this, this._setScenario));
                }
                break;
            case "external_urls":
                entity = entity as ExternalUrl;
                if (entity) {
                    this.hideNode(this.embeddedEditorContainer, false);
                    this.embeddedEditor = new ExternalUrlEditor();
                    this.embeddedEditor.setIsNew(isNew);
                    this.embeddedEditor.setReloadAfterSave(false);
                    this.embeddedEditor.setup(null, entity, "node", this.embeddedEditorContainer);
                    if (isNew) this.embeddedEditor.on("clickedSave", lang.hitch(this, this._setScenario));
                } else if (!entity && isNew) {
                    this.hideNode(this.embeddedEditorContainer, false);
                    this.embeddedEditor = new ExternalUrlEditor();
                    this.embeddedEditor.setIsNew(isNew);
                    this.embeddedEditor.setReloadAfterSave(false);
                    this.embeddedEditor.setup(null, null, "node", this.embeddedEditorContainer);
                    this.embeddedEditor.on("clickedSave", lang.hitch(this, this._setScenario));
                }
                break;
        }

        if (this.embeddedEditor) this.embeddedEditor.on("saved", lang.hitch(this, this._onClickReloadList, null));
    }

    private _setScenario() {
        if (this.embeddedEditor) this.embeddedEditor.setAttributeValue("refScenario", this.getAttributeValue("___root"), true);
    }

    private _resetEmbeddedEditor(): void {
        if (this.embeddedEditor) {
            this.embeddedEditor.RequestManager.cancelRunningRequests();
            this.embeddedEditor.destroy();
        }
        this.UiModder.domConstruct.empty(this.embeddedEditorContainer);
        this.embeddedEditor = null;
        this.hideNode(this.embeddedEditorContainer, true);
    }

    private _onClickNewListEntity(evt: MouseEvent): void {
        let source = this.getAttributeValue("source");
        let refEntity: Block | ExternalUrl;
        switch (source) {
            case "blocks":
                refEntity = {
                    ID: StringHelpers.randomID(),
                    $Displayname: this.translate("Screenplay_Heading_NewBlock"),
                    Classname: "",
                    ClassPrintname: "",
                    content_change_interval: null,
                    description: "",
                    display_mode: "image",
                    EL_ht_screenplay_block_ev_booking: [],
                    filter_file: "",
                    ref_filterparameter_entity_1: null,
                    ref_filterparameter_entity_2: null,
                    ref_filterparameter_entity_3: null,
                    ref_filterparameter_entity_4: null,
                    type: "",
                    is_new: true,
                };
                break;
            case "external_urls":
                refEntity = {
                    ID: StringHelpers.randomID(),
                    $Displayname: this.translate("Screenplay_Heading_NewExternalUrl"),
                    Classname: "",
                    ClassPrintname: "",
                    content_change_interval: null,
                    description: "",
                    web_url: "",
                    is_new: true,
                };
                break;
        }
        this._embedEditor(refEntity, true, null, lang.hitch(this, this._embedEditor, refEntity, true));
    }

    private _createNewHtEntity(refEntity: Block | ExternalUrl): void {
        let source = this.getAttributeValue("source");

        switch (source) {
            case "blocks":
                let newHtBlock: HTScenarioBlock = {
                    ID: StringHelpers.randomID(),
                    $Displayname: "",
                    Classname: "",
                    ClassPrintname: "",
                    order: undefined,
                    ref_screenplay_block: refEntity as Block,
                    ref_screenplay_scenario: this.getAttributeValue("___root"),
                    selected: true
                }
                this.store.addEntry(newHtBlock);
                break;
            case "external_urls":
                let newHtExternalUrl: HtScenarioExternalUrl = {
                    $Displayname: "",
                    Classname: "",
                    ClassPrintname: "",
                    ID: StringHelpers.randomID(),
                    order: undefined,
                    ref_screenplay_external_url: refEntity as ExternalUrl,
                    ref_screenplay_scenario: this.getAttributeValue("___root"),
                    selected: true
                }
                this.store.addEntry(newHtExternalUrl);
                break;
        }
       
    }

    private _onClickReloadList(callback?: Function): void {
        this.table.StandbyStart();

        let entityId = this.getAttributeValue("ID");
        let source = this.getAttributeValue("source");

        if (callback && typeof callback === "function") this.on("listReloaded", lang.hitch(this, callback));

        this.RequestManager.callfunction("Webtool_API_Screenplay__Scenario_LoadListValues", { entityId: entityId, source: source }, lang.hitch(this, this._onReloadListResponse), lang.hitch(this, this._onReloadListError));
    }

    private _onReloadListResponse(response: Array<HTScenarioBlock | HtScenarioExternalUrl>) {
        this.table.StandbyStop();

        this.store.setup(response);
        this._resetEmbeddedEditor();
        this._setExpanderMarking();
        this.setAttributeValue("deletedDiffData", null);
        this.setAttributeValue("changedDiffData", null);

        this.emit("listReloaded");
        this.table.updateTable();

        this._setNewButtonState();
    }

    private _onReloadListError(error) {
        this.table.StandbyStop();
        console.error(error);
    }

    setup(tabContainer?: MdsLax<InstanceType<typeof TabContainer>>, entity?: Scenario, mode: uiFrameTypes.DisplayMode = "tab", node?: HTMLElement): void {
        let heading;



        if (entity) {
            this.internalName = "scenarioEditor_" + entity.ID;
            if (entity.is_new) {
                this.setData(entity);
                heading = this.translate("Screenplay_Heading_NewScenario");
            } else {
                heading = `${this.translate("Screenplay_Heading_Scenario")}: ${entity.description}`;
                entity.is_new = false;
                this.setStartupState("ready");
                this._loadEntity(entity.ID);
            }

        } else {
            this.internalName = "scenarioEditor_New";
            let newEntity: Scenario = {
                ID: "",
                $Displayname: "",
                Classname: "",
                ClassPrintname: "",
                description: "",
                source: "",
                is_new: true,
                EL_ht_screenplay_scenario_block: [],
                EL_ht_screenplay_scenario_ext_urls: []
            }
            entity = newEntity;
            this.setData(entity);
            heading = this.translate("Screenplay_Heading_NewScenario");
        }

        if (tabContainer) {
            this.show("tab", heading, tabContainer.name);
        } else if (node && mode == "node") {
            this.show(mode, heading, null, node);
        } else {
            this.show("dlg", heading, tabContainer.name);
        }

        this.createDefaultButton("save", lang.hitch(this, this._onClickSave), this.isNew, "primary");
    }

    private _loadEntity(entityId: string): void {
        this.StandbyStart();
        this._deactivateDataButtons(true, true);
        this.RequestManager.callfunction(
            "Webtool_API_Screenplay__Scenario_LoadEntity",
            { ID: entityId },
            lang.hitch(this, this._onLoadEntityResponse),
            lang.hitch(this, this._onLoadEntityError));
    }

    private _onLoadEntityResponse(response: Scenario): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        response.is_new = false;
        this.setData(response);
    }

    private _onLoadEntityError(error): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.error(error);
    }

    private _onClickSave(): void {

        this.emit("clickedSave");

        if (this.embeddedEditor && this.embeddedEditor.isDirty()) {
            this._closeEditor(null, null, lang.hitch(this, this._onClickSave));
            return;
        }

        this.StandbyStart();
        this._deactivateDataButtons(true, true);



        let entityId = this.getAttributeValue("ID");
        let classname = this.getAttributeValue("Classname");
        let isNew = this.getAttributeValue("is_new");
        let changedData = this.getChangedData();
        this.RequestManager.callfunction(
            "Webtool_API_Screenplay__Scenario_SaveEntity",
            { ID: entityId, Classname: classname, is_new: isNew, changedData: changedData },
            lang.hitch(this, this._onSaveResponse),
            lang.hitch(this, this._onSaveError));
    }

    private _onSaveResponse(response): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.debug(response);
        this.emit("changed");
        this.emit("saved");
        if (this.reloadAfterSave) this._loadEntity(response.entityId);
    }

    private _onSaveError(error): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.error(error);
    }

    private _onClickRollback(e): void {
        this.rollback();
        this._resetEmbeddedEditor();
    }

    private _onClickReload(e): void {
        let entity = this.getAttributeValue("___root");
        this._loadEntity(entity.ID);
    }

    private _onClickDelete(e): void {
        this.StandbyStart();
        this._deactivateDataButtons(true, true);
        let entityId = this.getAttributeValue("ID");
        let classname = this.getAttributeValue("Classname");
        this.RequestManager.callfunction(
            "Webtool_API_Screenplay__Scenario_DeleteEntity",
            { ID: entityId, Classname: classname },
            lang.hitch(this, this._onDeleteResponse),
            lang.hitch(this, this._onDeleteError));
    }

    private _onDeleteResponse(response): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.debug(response);
        this.emit("changed");
        this.close();
    }

    private _onDeleteError(error): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.error(error);
    }

    private _deactivateDataButtons(deactivate: boolean, deactivateAll?: boolean): void {
        if (!this.isNew) this.requestContextButtonDisable("save", deactivate);
        this.requestContextButtonDisable("rollback", deactivate);
        if (deactivateAll) {
            this.requestContextButtonDisable("delete", deactivate);
            this.requestContextButtonDisable("reload", deactivate);
        }
    }

    _onCloseRequest() {
        let hasChanged = this.isDirty() === true;

        if (hasChanged) {
            let dlgConfirm = new GenericDialog();
            dlgConfirm.show(this.translate("Screenplay_Scenario_AskSavePendingChanges"),
                this.translate("General_PendingChanges") + " - " + this.title,
                "",
                "",
                "yesnocancel",
                lang.hitch(this, this._confirmCallback));

            return false;

        } else {
            return true;
        }
    }

    private _confirmCallback(result) {
        if (result == -1) { // nein
            this.emit("notSaved");
            this.close();
        } else if (result == 0) { // cancel
            this.emit("canceled");
        }
        else {
            this._onClickSave();
            this.on("saved", lang.hitch(this, this.close));
        }
    }

    protected afterSetData(): void {
        super.afterSetData.apply(this, arguments as any);

        this._resetEmbeddedEditor();
        this._resetSelectedEntities();

        let isNew = this.getAttributeValue("is_new");
        if (!isNew) {
            this.hideNode(this.sectionTableContainer, false);
            this.hideNode(this.tableContainer, false);

            this.createDefaultButton("delete", lang.hitch(this, this._onClickDelete), true, "secondary");
            this.createDefaultButton("rollback", lang.hitch(this, this._onClickRollback), false, "secondary");
            this.createDefaultButton("reload", lang.hitch(this, this._onClickReload), true, "primary");
            this.changeInternalName("scenarioEditor_" + this.getAttributeValue("ID"));
            this.setTitles(`${this.translate("Screenplay_Heading_Scenario")}: ${this.getAttributeValue("description")}`);
        } else {

        }
        this._deactivateDataButtons(true);
        if (this.isNew) this.enableButton("save", true);

        let source = this.getAttributeValue("source");
        this.inputSource.setValue(source, true);
        this._onChangeSource(source);

        if (source) {
            this.setupTable();
            this._setTableStore(source);
        }

        this.setAttributeValue("deletedDiffData", null);
        this.setAttributeValue("changedDiffData", null);
    }

    private _onChangeSource(source: string): void {
        this.setAttributeValue("source", source);
        let isNew = this.getAttributeValue("is_new");

        if (!isNew) {
            switch (source) {
                case "blocks":

                    this.hideNode(this.sectionTableContainer, false);
                    this.tableContainer.setHeading(this.translate("Scenario_Label_Blocks"))
                    break;
                case "external_urls":
                    this.hideNode(this.sectionTableContainer, false);
                    this.tableContainer.setHeading(this.translate("Scenario_Label_ExternalUrls"))
                    break;
                default:
                    this.hideNode(this.sectionTableContainer, true);
                    break;
            }
            if (this.table) {
                this._setTableLayout(source);
                this._setTableStore(source);
            } else if (source) {
                this.setupTable();
            }
            this._resetEmbeddedEditor();
        }
    }

    protected _onChanged(attribute: string, newValue: any): void {
        switch (attribute) {
            case "#source":
                this._onChangeSource(newValue ? newValue.value : null);
                break;
        }
    }

    protected onDirtyChange(newState: boolean) {
        this._deactivateDataButtons(!newState);
        this.setDirtyMarker(newState);
    }

    private _openSED(mode: string, evt: MouseEvent): void {
        switch (mode) {
            case "selectExistingEntity":
                let source = this.getAttributeValue("source");
                switch (source) {
                    case "blocks":
                        this.loadSelectEntityDialog(evt, "selectBlock", lang.hitch(this, this._onSEDCallback));
                        break;
                    case "external_urls":
                        this.loadSelectEntityDialog(evt, "selectExternalUrl", lang.hitch(this, this._onSEDCallback));
                        break;
                }
                break;
        }
    }

    private _onSEDCallback(mode: string, data: Array<any>, items: Array<SelectEntityDialogTypes.Item<any>>) {
        switch (mode) {
            case "selectBlock":
            case "selectExternalUrl":
                if (items && items.length > 0) {
                    let entity = items[0].entry.data;
                    entity.is_new = false;
                    if (this._getHtEntity(entity)) {
                        this.showToast(this.translate("Scenario_Warning_HtEntityExists"), "fas fa-times");
                    } else {
                        this._embedEditor(entity, true, null, lang.hitch(this, this._embedEditor, entity, true));
                    }
                }
                break;
        }
    }

    private _onClicKNew(evt: MouseEvent) {
        let floatingmenu = new FloatingMenu();

        let newEntityLabel;
        let existingEntityLabel;

        let source = this.getAttributeValue("source");
        switch (source) {
            case "blocks":
                newEntityLabel = "Scenario_Label_NewBlockButton";
                existingEntityLabel = "Scenario_Label_ExistingBlockButton";
                break;
            case "external_urls":
                newEntityLabel = "Scenario_Label_NewExternalUrlButton";
                existingEntityLabel = "Scenario_Label_ExistingExternalUrlButton";
                break;
        }

        floatingmenu.addEntry({
            callback: lang.hitch(this, this._onClickNewListEntity),
            label: this.translate(newEntityLabel),
            enabled: true,
            icon: "fas fa-plus",
            name: "newEntity"
        });
        floatingmenu.addEntry({
            callback: lang.hitch(this, this._openSED, "selectExistingEntity", evt),
            label: this.translate(existingEntityLabel),
            enabled: true,
            icon: "fas fa-plus",
            name: "addExistingEntity"
        });
        floatingmenu.open(evt);
    }

    postCreate(): void {
        super.postCreate.apply(this, arguments as any);

        this.inputSource.setCallfunction("Webtool_API_Screenplay__Scenario_GetSources");
        this.inputSource.loadListValues();
        this.inputSource.on("change", lang.hitch(this, this._onChangeSource));

        this.SEDconfigCallfunction = "Webtool_API_Screenplay__Scenario_GetSEDConfig";
        
    }

    startup(): void {
        super.startup.apply(this, arguments as any);
    }
}

export default mdsWidget(ScenarioEditor);